import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { commsApi } from '../phase567Api';
import { useAuth } from '../AuthContext';
import { toast } from '../../hooks/use-toast';
import { errorMessage } from '../errors';
import { formatPhone } from '../shared/phone';
import PhoneInput from '../shared/PhoneInput';

// Ticket 195: the opt-out list the send gates already enforce. Any staff seat
// adds; only "Manage staff" holders (backend ACTION_KEYS["suppression.remove"]
// -> staff.manage) remove, and the reason is logged.
export function canRemoveSuppression(user) {
  return !!(user?.is_super_admin || (user?.permissions || []).includes('staff.manage'));
}

const CHANNEL_LABELS = { sms: 'Text', email: 'Email' };
const REASON_LABELS = { user_stopped: 'Replied STOP', manual: 'Added by staff' };

export function reasonLabel(reason) {
  return REASON_LABELS[reason] || String(reason || '').replace(/_/g, ' ') || 'Added by staff';
}

function addedLine(row) {
  const when = row.added_at ? new Date(row.added_at) : null;
  const date = when && !Number.isNaN(when.getTime()) ? when.toLocaleDateString() : '';
  return [row.added_by ? `by ${row.added_by_name || 'a teammate'}` : '', date].filter(Boolean).join(' · ');
}

export default function SuppressionDialog({ open, onOpenChange }) {
  const canRemove = canRemoveSuppression(useAuth()?.user);
  const [rows, setRows] = useState(null);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState(false);
  const [form, setForm] = useState({ channel: 'sms', address: '', reason: '' });
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null); // { key, reason }
  const [removeBusy, setRemoveBusy] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    try {
      const r = await commsApi.listSuppression();
      setRows(r?.items || []);
      setTruncated(Boolean(r?.truncated));
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setRows(null);
    setRemoving(null);
    load();
  }, [open, load]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.address.trim() || adding) return;
    setAdding(true);
    try {
      const r = await commsApi.addSuppression({
        channel: form.channel,
        address: form.address.trim(),
        ...(form.reason.trim() ? { reason: form.reason.trim() } : {}),
      });
      setForm({ ...form, address: '', reason: '' });
      toast({ title: r?.already_listed ? 'Already on the list' : 'Added to the opt-out list' });
      await load();
    } catch (err) {
      toast({ title: "Couldn't add the opt-out", description: errorMessage(err), variant: 'destructive' });
    } finally { setAdding(false); }
  };

  const remove = async (row) => {
    if (removeBusy || removing.reason.trim().length < 3) return;
    setRemoveBusy(true);
    try {
      await commsApi.removeSuppression(row.channel, row.address, removing.reason.trim());
      setRemoving(null);
      await load();
    } catch (err) {
      toast({ title: "Couldn't remove the opt-out", description: errorMessage(err), variant: 'destructive' });
    } finally { setRemoveBusy(false); }
  };

  const rowKey = (r) => `${r.channel}:${r.address}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="suppression-dialog">
        <DialogHeader>
          <DialogTitle>Opt-out list</DialogTitle>
          <DialogDescription>
            People we must not text or email. Sends to these addresses are blocked.
            {canRemove
              ? ' Removing one needs a reason, which is logged.'
              : ' Only an admin can remove one.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={add} className="space-y-2" data-testid="suppression-add-form">
          <div className="flex gap-1" role="group" aria-label="Channel">
            {['sms', 'email'].map((ch) => (
              <button key={ch} type="button" aria-pressed={form.channel === ch}
                onClick={() => setForm({ ...form, channel: ch, address: '' })}
                className={`px-3 py-1 rounded-full text-sm border ${form.channel === ch ? 'border-[#070B5F] bg-[#070B5F]/5' : 'border-slate-200'}`}
                data-testid={`suppression-channel-${ch}`}>
                {CHANNEL_LABELS[ch]}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            {form.channel === 'sms' ? (
              <PhoneInput value={form.address} aria-label="Phone number"
                onChange={(digits) => setForm({ ...form, address: digits })}
                data-testid="suppression-address" />
            ) : (
              <Input value={form.address} aria-label="Email address" placeholder="name@example.com"
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                data-testid="suppression-address" />
            )}
            <button type="submit" className="btn-gold text-sm !px-4 !py-1.5 whitespace-nowrap"
              disabled={!form.address.trim() || adding} data-testid="suppression-add">
              {adding && <Loader2 className="animate-spin" size={14} />} Add
            </button>
          </div>
          <Input value={form.reason} maxLength={200} aria-label="Reason (optional)" placeholder="Reason (optional)"
            onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="suppression-reason" />
        </form>

        {error ? (
          <p className="text-sm text-red-600">
            Couldn't load the list. <button type="button" className="underline" onClick={load}>Retry</button>
          </p>
        ) : rows === null ? (
          <p className="text-sm text-slate-500 flex items-center gap-1"><Loader2 className="animate-spin" size={14} /> Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="suppression-empty">Nobody is on the opt-out list.</p>
        ) : (
          <>
            <ul className="divide-y divide-slate-100 max-h-72 overflow-y-auto" data-testid="suppression-list">
              {rows.map((r) => (
                <li key={rowKey(r)} className="py-2" data-testid={`suppression-row-${rowKey(r)}`}>
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-800 truncate">
                        {r.channel === 'sms' ? formatPhone(r.address) : r.address}
                        <span className="text-[10px] font-normal text-slate-500 ml-2">{CHANNEL_LABELS[r.channel] || r.channel}</span>
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {reasonLabel(r.reason)}{addedLine(r) ? ` · ${addedLine(r)}` : ''}
                      </div>
                    </div>
                    {canRemove && (
                      <button type="button" aria-label={`Remove ${r.address}`}
                        className="p-1 text-slate-500 hover:text-red-700"
                        onClick={() => setRemoving({ key: rowKey(r), reason: '' })}
                        data-testid={`suppression-remove-${rowKey(r)}`}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  {removing?.key === rowKey(r) && (
                    <div className="mt-2 flex gap-2" data-testid="suppression-remove-form">
                      <Input value={removing.reason} maxLength={500} autoFocus aria-label="Reason for removing"
                        placeholder="Why is this being removed?"
                        onChange={(e) => setRemoving({ ...removing, reason: e.target.value })}
                        data-testid="suppression-remove-reason" />
                      <button type="button" className="text-sm px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50"
                        onClick={() => setRemoving(null)}>Cancel</button>
                      <button type="button" className="text-sm px-3 py-1.5 rounded-md bg-red-700 text-white disabled:opacity-50 whitespace-nowrap"
                        disabled={removing.reason.trim().length < 3 || removeBusy}
                        onClick={() => remove(r)} data-testid="suppression-remove-confirm">
                        {removeBusy && <Loader2 className="animate-spin inline mr-1" size={12} />} Confirm remove
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
            {truncated && (
              <p className="text-xs text-slate-500" data-testid="suppression-truncated">
                Showing the 500 most recent. Older entries still block sends.
              </p>
            )}
          </>
        )}
        <p className="text-xs text-slate-500">
          Removing an entry does not restore a contact's consent. A contact marked
          "revoked" stays blocked until their consent is updated.
        </p>
      </DialogContent>
    </Dialog>
  );
}
